import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

const API = `/api`;

const EASE = [0.22, 1, 0.36, 1];

const ETAPES = [
  {
    num: "01",
    titre: "L'idée",
    texte:
      "Le projet est né d'une conviction simple : le droit pratiqué au quotidien produit une connaissance précieuse, qui se perd trop souvent. L'objectif était de constituer, dossier après dossier, une base de connaissances juridiques — alimentée de manière strictement anonyme à partir des dossiers traités par le cabinet.",
  },
  {
    num: "02",
    titre: "Le défi",
    texte:
      "L'outil de l'époque — un site WordPress — ne permettait pas de structurer ni de gérer correctement une telle base. Faute d'un environnement adapté, le projet a progressivement été mis en sommeil.",
  },
  {
    num: "03",
    titre: "Le renouveau",
    texte:
      "L'arrivée de l'intelligence artificielle a changé la donne. Elle a permis de reconstruire entièrement le site et de rendre les fiches accessibles dans une structure claire : trois pôles, douze thèmes, un moteur de recherche et un assistant capable de répondre aux questions à partir des fiches.",
  },
  {
    num: "04",
    titre: "Aujourd'hui",
    texte:
      "Le cabinet réfléchit à la relance du projet. Dans l'intervalle, les fiches sont là : librement consultables, telles quelles.",
  },
];

export default function APropos() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/knowledge`)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const poles = new Set();
        const themes = new Set();
        data.forEach((f) => {
          (f.poles || []).forEach((p) => poles.add(p));
          (f.themes || []).forEach((t) => themes.add(t));
        });
        setStats({ count: data.length, poles: poles.size, themes: themes.size });
      })
      .catch(() => {});
  }, []);

  return (
    <div className="pt-40 pb-24 sm:pb-32 min-h-screen" data-testid="apropos-page">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <Link
            to="/ressources"
            data-testid="apropos-back-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A0A5B1] hover:text-gold transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Ressources
          </Link>
          <div className="mt-8">
            <span className="label-accent" data-testid="apropos-overline">À propos</span>
            <h1 className="mt-6 font-serif font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
              Le projet derrière{" "}
              <em className="italic text-gold-gradient not-italic">les fiches.</em>
            </h1>
            <p className="mt-6 text-sm sm:text-base text-[#A0A5B1] max-w-2xl leading-relaxed">
              Cette base de connaissances n'est pas née d'un plan marketing, mais du travail
              quotidien du cabinet. En quatre temps, voici d'où elle vient — et où elle en est.
            </p>
          </div>
        </motion.div>

        <div className="mt-16 sm:mt-20 max-w-3xl">
          {ETAPES.map((e, i) => (
            <motion.div
              key={e.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
              className="relative pl-10 sm:pl-14 pb-12 border-l border-gold/20 last:pb-0"
              data-testid={`apropos-etape-${e.num}`}
            >
              <span className="absolute -left-px top-0 h-8 w-px bg-gold" />
              <span className="font-mono text-[10px] tracking-[0.3em] text-gold/70">{e.num}</span>
              <h2 className="mt-2 font-serif font-light text-xl sm:text-2xl text-[#FAF8F5]">
                {e.titre}
              </h2>
              <p className="mt-3 text-sm sm:text-base text-[#A0A5B1] leading-relaxed">{e.texte}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-16 max-w-3xl border border-white/10 bg-ink-card p-8 sm:p-10"
          data-testid="apropos-chiffres"
        >
          <span className="label-accent">En chiffres</span>
          <div className="mt-6 grid grid-cols-3 gap-6">
            <div>
              <p className="font-serif font-light text-3xl sm:text-4xl text-gold-gradient" data-testid="apropos-stat-fiches">
                {stats !== null ? stats.count : "…"}
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626773]">Fiches en ligne</p>
            </div>
            <div>
              <p className="font-serif font-light text-3xl sm:text-4xl text-gold-gradient" data-testid="apropos-stat-poles">{stats !== null ? stats.poles : "…"}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626773]">Pôles</p>
            </div>
            <div>
              <p className="font-serif font-light text-3xl sm:text-4xl text-gold-gradient" data-testid="apropos-stat-themes">{stats !== null ? stats.themes : "…"}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626773]">Thèmes</p>
            </div>
          </div>
          <p className="mt-8 pt-6 border-t border-white/10 text-xs text-[#626773] leading-relaxed">
            Les fiches sont fournies à titre purement informatif : elles ne constituent ni une
            consultation juridique, ni un avis applicable à votre situation. En consultant ce
            portail, vous acceptez nos{" "}
            <Link
              to="/conditions-utilisation-portail"
              data-testid="apropos-terms-link"
              className="text-gold/90 underline decoration-gold/40 underline-offset-4 hover:text-gold"
            >
              conditions d'utilisation de la base de connaissance
            </Link>
            . Pour toute question concrète,{" "}
            <a href="/#contact" className="text-gold/90 underline decoration-gold/40 underline-offset-4 hover:text-gold">
              contactez le cabinet
            </a>
            .
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-20 sm:mt-24 max-w-3xl"
          data-testid="apropos-nom"
        >
          <span className="label-accent">Pourquoi e² ?</span>
          <div className="mt-8 space-y-5">
            <p className="font-serif font-light text-xl sm:text-2xl leading-snug text-[#FAF8F5]">
              <span className="text-gold">e</span>ntreprise — parce que c'est la vôtre qui nous occupe.
            </p>
            <p className="font-serif font-light text-xl sm:text-2xl leading-snug text-[#FAF8F5]">
              <span className="text-gold">e</span>xigence — dans le raisonnement, et dans la forme
              quand elle sert la clarté.
            </p>
            <p className="font-serif font-light text-xl sm:text-2xl leading-snug text-[#FAF8F5]">
              <span className="text-gold">e</span>xtranéité — pas un cabinet mondial ; mais vos dossiers
              franchissent les frontières, et nous les suivons avec nos correspondants.
            </p>
            <p className="font-serif font-light text-xl sm:text-2xl leading-snug text-[#FAF8F5]">
              <span className="text-gold">e</span>mpowerment — vous rendre autonome,
              à commencer par ce portail.
            </p>
          </div>
          <p className="mt-10 text-sm sm:text-base text-[#A0A5B1] leading-relaxed max-w-2xl">
            Le e est au carré : l'effet démultiplicateur de la combinaison. Le « .law »,
            réservé aux professions juridiques, dit le reste.
          </p>
          <p className="mt-4 text-sm sm:text-base text-[#A0A5B1] leading-relaxed max-w-2xl">
            « e carré », « e two », « e square », « e kwadraat » — toutes nous vont :
            l'adaptabilité commence par le nom.
          </p>
          <p className="mt-4 text-sm text-[#626773] leading-relaxed max-w-2xl italic">
            Évidemment, pour être retrouvé sur Google ou LinkedIn, on a vu plus simple.
            C'est comme ça — on assume.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-14 flex flex-wrap items-center gap-6"
        >
          <Link
            to="/ressources"
            data-testid="apropos-cta-ressources"
            className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-8 py-3.5 hover:bg-gold-light transition-colors"
          >
            Parcourir les fiches <ArrowUpRight className="w-4 h-4" />
          </Link>
          <a
            href="/#contact"
            data-testid="apropos-cta-contact"
            className="text-xs uppercase tracking-[0.25em] text-[#A0A5B1] hover:text-gold transition-colors border-b border-transparent hover:border-gold/40 pb-1"
          >
            Contacter le cabinet
          </a>
        </motion.div>
      </div>
    </div>
  );
}
